import React from 'react';
import { LegalLayout } from '@/components/layouts/LegalLayout';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Calendar, RefreshCw, Headphones } from 'lucide-react';

export const ConditionsVente: React.FC = () => {
  return (
    <LegalLayout>
      <div className="prose prose-slate dark:prose-invert max-w-none">
        <div className="flex items-center gap-3 mb-6">
          <h1 className="text-3xl font-bold">Conditions Générales de Vente</h1>
          <Badge variant="secondary">SaaS</Badge>
        </div>
        
        <p className="text-muted-foreground mb-8">
          Dernière mise à jour : [Date - À remplir]
        </p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            1. Tarifs et abonnements
          </h2>
          
          <div className="grid gap-4">
            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-3">💰 Formules d'abonnement</h3>
              <div className="bg-muted/30 p-3 rounded">
                <ul className="text-sm space-y-1">
                  <li><strong>Starter :</strong> [Prix mensuel - À remplir] € HT/mois</li>
                  <li><strong>Professional :</strong> [Prix mensuel - À remplir] € HT/mois</li>
                  <li><strong>Enterprise :</strong> [Prix mensuel - À remplir] € HT/mois</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-2">
                  Réduction de [X]% sur les abonnements annuels
                </p>
              </div>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-3">📋 Tarifs détaillés</h3>
              <p className="text-sm mb-2">Les tarifs sont exprimés :</p>
              <ul className="text-sm space-y-1">
                <li>• Hors taxes (TVA [XX]% en sus pour la France)</li>
                <li>• En euros (EUR)</li>
                <li>• Par période d'abonnement (mensuel ou annuel)</li>
                <li>• Susceptibles d'évoluer avec préavis de 30 jours</li>
              </ul>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            2. Modalités de paiement
          </h2>
          
          <div className="space-y-4">
            <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">💳 Moyens de paiement acceptés</h3>
              <ul className="text-sm space-y-1">
                <li>• Carte bancaire (Visa, Mastercard, American Express)</li>
                <li>• Prélèvement SEPA (pour les clients européens)</li>
                <li>• Virement bancaire (sur demande pour les gros volumes)</li>
              </ul>
            </div>

            <div className="bg-muted/30 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">🔄 Renouvellement automatique</h3>
              <p className="text-sm">
                L'abonnement se renouvelle automatiquement à chaque échéance sauf résiliation. 
                Le paiement est prélevé le même jour que la souscription initiale.
              </p>
            </div>

            <div className="border border-orange-200 bg-orange-50 dark:bg-orange-950/20 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">⚠️ Défaut de paiement</h3>
              <ul className="text-sm space-y-1">
                <li>• Suspension de l'accès après 7 jours d'impayé</li>
                <li>• Frais de relance : [X]€ par relance</li>
                <li>• Résiliation possible après 30 jours d'impayé</li>
              </ul>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">3. Facturation</h2>
          
          <div className="grid gap-4">
            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-2">📄 Émission des factures</h3>
              <ul className="text-sm space-y-1">
                <li>• Facture émise automatiquement à chaque paiement</li>
                <li>• Envoi par email sous 24h maximum</li>
                <li>• Accessible depuis votre espace client</li>
                <li>• Format PDF avec mentions légales obligatoires</li>
              </ul>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-2">💼 Informations de facturation</h3>
              <p className="text-sm mb-2">Veillez à maintenir à jour :</p>
              <ul className="text-sm space-y-1">
                <li>• Raison sociale et adresse de facturation</li>
                <li>• Numéro de TVA intracommunautaire (si applicable)</li>
                <li>• Email de réception des factures</li>
              </ul>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            4. Droit de rétractation
          </h2>
          
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">✅ Période d'essai gratuite</h3>
              <p className="text-sm">
                Nous offrons une période d'essai gratuit de [X] jours permettant de tester 
                toutes les fonctionnalités sans engagement.
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">🔄 Droit de rétractation (14 jours)</h3>
              <p className="text-sm mb-2">
                Conformément au Code de la consommation, vous disposez de 14 jours pour exercer 
                votre droit de rétractation à compter de la souscription.
              </p>
              <p className="text-sm">
                <strong>Exception :</strong> Ce droit ne s'applique pas si vous avez expressément 
                demandé l'exécution immédiate du service.
              </p>
            </div>

            <div className="bg-muted/30 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">💰 Modalités de remboursement</h3>
              <ul className="text-sm space-y-1">
                <li>• Remboursement intégral dans les 14 jours</li>
                <li>• Retour sur le moyen de paiement initial</li>
                <li>• Aucun frais de dossier</li>
              </ul>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">5. Résiliation</h2>
          
          <div className="grid gap-4">
            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-2">📧 Résiliation par le client</h3>
              <ul className="text-sm space-y-1">
                <li>• Sans préavis depuis votre espace client</li>
                <li>• Ou par email à [resiliation@votredomaine.com - À remplir]</li>
                <li>• Accès maintenu jusqu'à la fin de la période payée</li>
                <li>• Pas de remboursement au prorata</li>
              </ul>
            </div>

            <div className="border border-orange-200 bg-orange-50 dark:bg-orange-950/20 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">⚠️ Résiliation par nos soins</h3>
              <p className="text-sm">
                Possible en cas de non-paiement, violation des CGU/CGV, ou usage abusif. 
                Préavis de 15 jours sauf urgence.
              </p>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Headphones className="w-5 h-5" />
            6. Support et SLA
          </h2>
          
          <div className="grid gap-4">
            <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">🕐 Niveaux de support</h3>
              <ul className="text-sm space-y-1">
                <li><strong>Email :</strong> Réponse sous 24h ouvrées</li>
                <li><strong>Chat :</strong> Lundi-Vendredi 9h-18h</li>
                <li><strong>Urgence :</strong> [Téléphone pour clients Premium - À remplir]</li>
              </ul>
            </div>

            <div className="bg-muted/30 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">📊 Engagement de disponibilité</h3>
              <ul className="text-sm space-y-1">
                <li>• Objectif de disponibilité : 99,5% sur le mois</li>
                <li>• Maintenance programmée en dehors des heures ouvrées</li>
                <li>• Information préalable des interruptions planifiées</li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 p-4 rounded-lg">
              <p className="text-sm">
                <strong>Contact support :</strong> [support@votredomaine.com - À remplir]<br/>
                <strong>Horaires :</strong> Lundi-Vendredi, 9h-18h (hors jours fériés)
              </p>
            </div>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
};